/**
 * «Выписки → ИП» и «Выписки → Банки»: справочник — имя, код (ИНН/БИК), архив.
 *
 * Удаления нет, есть архив: на ИП и банк ссылаются загруженные выписки.
 * Архивный не предлагается при загрузке, но в списке выписок остаётся.
 */
import { useCallback, useEffect, useState } from "react";

import { NoAccess } from "@/components/NoAccess";
import { STATEMENTS, useCan } from "@/lib/access";
import {
  createDirectoryRow,
  type DirectoryKind,
  type DirectoryRow,
  loadDirectory,
  patchDirectoryRow,
} from "@/lib/statements";

const INPUT =
  "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2.5 text-[13px] outline-none focus:border-border-strong";
const BUTTON =
  "h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[12.5px] hover:bg-bg-hover disabled:opacity-50";
const PRIMARY = "h-8 rounded-[var(--radius-ui)] bg-fg px-3 text-[12.5px] font-medium text-bg disabled:opacity-50";

export function DirectoryPage({ kind }: { kind: DirectoryKind }) {
  return useCan(STATEMENTS) ? <DirectoryScreen kind={kind} /> : <NoAccess what="выпискам" />;
}

function DirectoryScreen({ kind }: { kind: DirectoryKind }) {
  const [rows, setRows] = useState<DirectoryRow[] | null>(null);
  const [archived, setArchived] = useState(false);
  const [failure, setFailure] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<{ id: number; name: string; code: string } | null>(null);

  const reload = useCallback(() => {
    loadDirectory(kind, archived)
      .then(setRows)
      .catch((error: Error) => setFailure(error.message));
  }, [kind, archived]);

  useEffect(reload, [reload]);

  // Любое действие — один путь: запрос, перечитать список, ошибка словами сервера.
  const run = (action: () => Promise<unknown>, after?: () => void) => {
    setFailure("");
    setBusy(true);
    action()
      .then(() => {
        after?.();
        reload();
      })
      .catch((error: Error) => setFailure(error.message))
      .finally(() => setBusy(false));
  };

  const add = (event: React.FormEvent) => {
    event.preventDefault();
    run(
      () => createDirectoryRow(kind, name, code),
      () => {
        setName("");
        setCode("");
      },
    );
  };

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 [&>*]:flex-none">
      <form onSubmit={add} className="flex flex-wrap items-end gap-2">
        <label className="grid gap-1 text-[11.5px] text-fg-muted">
          название
          <input
            className={`${INPUT} w-[280px]`}
            value={name}
            placeholder={kind.placeholder}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="grid gap-1 text-[11.5px] text-fg-muted" title={kind.codeHint}>
          {kind.codeLabel}
          <input
            className={`${INPUT} w-[160px] font-mono`}
            value={code}
            inputMode="numeric"
            placeholder={kind.codeHint.split(";")[0]}
            onChange={(e) => setCode(e.target.value)}
          />
        </label>
        <button type="submit" disabled={busy || name.trim() === ""} className={PRIMARY}>
          Добавить
        </button>
        <label className="ml-auto flex items-center gap-2 text-[12.5px] text-fg-muted">
          <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
          показать архив
        </label>
      </form>

      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}

      {rows === null ? (
        <div className="text-[12.5px] text-fg-muted">…</div>
      ) : rows.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">Пусто — добавьте первую запись.</div>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                <th className="border-b border-border px-3 py-2 text-left">название</th>
                <th className="border-b border-border px-3 py-2 text-left">{kind.codeLabel}</th>
                <th className="w-[1%] border-b border-border px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isEditing = editing?.id === row.id;
                return (
                  <tr key={row.id} className={`hover:bg-bg-hover ${row.archived_at ? "text-fg-faint" : ""}`}>
                    <td className="border-b border-border px-3 py-1.5">
                      {isEditing ? (
                        <input
                          className={`${INPUT} w-full`}
                          value={editing.name}
                          autoFocus
                          onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                        />
                      ) : (
                        <>
                          {row.name}
                          {row.archived_at ? <span className="ml-2 text-[11px]">архив</span> : null}
                        </>
                      )}
                    </td>
                    <td className="border-b border-border px-3 py-1.5 font-mono">
                      {isEditing ? (
                        <input
                          className={`${INPUT} w-[160px] font-mono`}
                          value={editing.code}
                          inputMode="numeric"
                          onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                        />
                      ) : (
                        (row.code ?? <span className="text-fg-faint">—</span>)
                      )}
                    </td>
                    <td className="whitespace-nowrap border-b border-border px-3 py-1.5 text-right">
                      <div className="flex justify-end gap-1.5">
                        {isEditing ? (
                          <>
                            <button
                              type="button"
                              className={PRIMARY}
                              disabled={busy}
                              onClick={() =>
                                run(
                                  () => patchDirectoryRow(kind, row.id, { name: editing.name, code: editing.code }),
                                  () => setEditing(null),
                                )
                              }
                            >
                              Сохранить
                            </button>
                            <button type="button" className={BUTTON} onClick={() => setEditing(null)}>
                              Отмена
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className={BUTTON}
                              onClick={() => setEditing({ id: row.id, name: row.name, code: row.code ?? "" })}
                            >
                              Изменить
                            </button>
                            <button
                              type="button"
                              className={BUTTON}
                              disabled={busy}
                              onClick={() => run(() => patchDirectoryRow(kind, row.id, { archived: !row.archived_at }))}
                            >
                              {row.archived_at ? "Вернуть" : "В архив"}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
